import { View } from "@-/com.npmjs.react-native";
import runes from "@-/com.npmjs.runes2";
import {
  Surface,
  SurfaceDescription,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { type WordLevel, formatWordLevelLabel } from "@-/dev.improve.haniverse.lib.level";
import { LockedLevelBlur } from "#locked-level-blur";
import { type WordLevelsListState } from "#use-word-levels-list-state";
import { WordLevelGlyphGrid } from "#word-level-glyph-grid";

/** Blurred placeholder that matches unlocked word tiles: one reading stub per line. */
function LockedWordPinyinPlaceholder({ word }: { word: string }) {
  return (
    <View className="size-full items-center justify-center py-0.5">
      {runes(word).map((_, index) => (
        <Text
          adjustsFontSizeToFit
          className="min-h-0 w-full flex-1 text-center text-sm text-foreground/70"
          key={index}
          numberOfLines={1}
        >
          ··
        </Text>
      ))}
    </View>
  );
}

function WordLevelSurfaceHeader({ level }: { level: WordLevel }) {
  return (
    <SurfaceHeader>
      <SurfaceTitle>{`Level ${formatWordLevelLabel(level)}`}</SurfaceTitle>
      <SurfaceDescription>{`${level.words.length} words`}</SurfaceDescription>
    </SurfaceHeader>
  );
}

/** A premium word level: blurred plain text, so it reads no card data. */
export function LockedWordLevelSurface({ columns, level }: { columns: number; level: WordLevel }) {
  return (
    <Surface className="mb-1" testID={`locked-word-level-${formatWordLevelLabel(level)}`}>
      <WordLevelSurfaceHeader level={level} />
      <LockedLevelBlur>
        <View aria-hidden className="gap-1 p-1">
          {Array.from({ length: Math.ceil(level.words.length / columns) }, (_, row) => (
            <View className="flex-row gap-1" key={row}>
              {Array.from({ length: columns }, (_, column) => {
                const word = level.words[row * columns + column];
                return (
                  <View className="aspect-square min-w-0 flex-1" key={column}>
                    {word === undefined ? null : (
                      <View className="size-full items-center justify-center rounded-xs border border-border px-0.5">
                        <LockedWordPinyinPlaceholder word={word} />
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      </LockedLevelBlur>
    </Surface>
  );
}

/**
 * A word level between two character levels. Like a character level, it is always shown: the
 * curriculum introduces its words at their place in the order, with no separate unlock step.
 */
export function WordLevelSurface({
  level,
  state: { columns, onNavigate, tileSize },
}: {
  level: WordLevel;
  state: WordLevelsListState;
}) {
  return (
    <Surface className="mb-1" testID={`word-level-${formatWordLevelLabel(level)}`}>
      <WordLevelSurfaceHeader level={level} />
      <WordLevelGlyphGrid
        columns={columns}
        onNavigate={onNavigate}
        tileSize={tileSize}
        words={level.words}
      />
    </Surface>
  );
}
